export type SupportedLocale = "en" | "es";

/** Fallback for content and client documents (invoices, reminders) with no language set. */
export const DEFAULT_LOCALE: SupportedLocale = "es";
/** Language of the public website when neither the visitor (?lang=, the switcher) nor the browser picks one. */
export const PUBLIC_DEFAULT_LOCALE: SupportedLocale = "en";
/** v3: every visitor got "es" saved while Spanish was the default, so the old key is ignored. */
export const WEBSITE_LOCALE_STORAGE_KEY = "website_locale_v3";

function isSupportedLocale(value: string | null): value is SupportedLocale {
  return value === "en" || value === "es";
}

export function getBrowserLocale(): SupportedLocale {
  if (typeof window === "undefined") {
    return PUBLIC_DEFAULT_LOCALE;
  }

  const locale = (navigator.language || "").toLowerCase();
  if (locale.startsWith("es")) return "es";
  if (locale.startsWith("en")) return "en";
  return PUBLIC_DEFAULT_LOCALE;
}

export function getInitialLocale(): SupportedLocale {
  if (typeof window === "undefined") {
    return PUBLIC_DEFAULT_LOCALE;
  }

  const searchLocale = new URLSearchParams(window.location.search).get("lang");
  if (isSupportedLocale(searchLocale)) {
    return searchLocale;
  }

  const savedLocale = localStorage.getItem(WEBSITE_LOCALE_STORAGE_KEY);
  if (isSupportedLocale(savedLocale)) {
    return savedLocale;
  }

  // First visit: the browser's language, English if it is neither English nor Spanish.
  return getBrowserLocale();
}

export function persistLocale(locale: SupportedLocale) {
  if (typeof window === "undefined") {
    return;
  }
  localStorage.setItem(WEBSITE_LOCALE_STORAGE_KEY, locale);
}

export function updateUrlLocale(locale: SupportedLocale) {
  if (typeof window === "undefined") {
    return;
  }

  // English (the default) is the clean URL; Spanish keeps ?lang=es, matching the hreflang links.
  const url = new URL(window.location.href);
  if (locale === PUBLIC_DEFAULT_LOCALE) url.searchParams.delete("lang");
  else url.searchParams.set("lang", locale);
  window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
}

export function getLocalizedValue<T>(value: unknown, locale: SupportedLocale): T | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (typeof value !== "object") {
    return value as T;
  }

  if (Array.isArray(value)) {
    return value as T;
  }

  const map = value as Record<string, unknown>;
  const localeValue = map[locale];
  if (localeValue !== undefined && localeValue !== null) {
    return localeValue as T;
  }

  const fallback = map[DEFAULT_LOCALE] ?? map.en ?? map.es;
  return fallback as T | undefined;
}

export function readLocalizedText(value: unknown, locale: SupportedLocale): string {
  if (typeof value === 'string') return value;
  const localized = getLocalizedValue<string>(value, locale);
  return typeof localized === 'string' ? localized : '';
}

export function writeLocalizedText(
  current: unknown,
  locale: SupportedLocale,
  next: string
): { en: string; es: string } {
  const en = locale === 'en' ? next : readLocalizedText(current, 'en');
  const es = locale === 'es' ? next : readLocalizedText(current, 'es');
  return { en, es };
}

export function readLocalizedJson<T>(value: unknown, locale: SupportedLocale, fallback: T): T {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const map = value as Record<string, unknown>;
    if ('en' in map || 'es' in map) {
      const picked = map[locale] ?? map.en ?? map.es;
      return (picked as T) ?? fallback;
    }
  }
  if (value === undefined || value === null) return fallback;
  return value as T;
}

export function writeLocalizedJson<T>(current: unknown, locale: SupportedLocale, next: T): { en: T; es: T } {
  const en = locale === 'en' ? next : readLocalizedJson<T>(current, 'en', next);
  const es = locale === 'es' ? next : readLocalizedJson<T>(current, 'es', next);
  return { en, es };
}
